/**
 * CORE — owned by the Core agent. Headless deterministic simulation.
 *
 * No DOM, no Three.js, no fetch. This module must run identically in the browser
 * AND inside a Cloudflare Worker (for server-side replay verification). It owns:
 *   - seeded course generation
 *   - the fixed-timestep Rapier simulation
 *   - scoring + the replay state hash
 *
 * This file is the LOCKED PUBLIC SURFACE of Core. The Architect defined these
 * signatures so Render/Shell/Backend can build against them today. The Core agent
 * fills in the bodies. Do not add game logic to other agents' modules.
 *
 * Determinism rules (non-negotiable):
 *   - Step at FIXED_DT only; never couple the sim to the render clock.
 *   - All generation randomness comes from makeRng(seed); the live sim is RNG-free.
 *   - Use @dimforge/rapier3d-compat at a single pinned version on client & server.
 *
 * IMPLEMENTATION NOTE (Core): the score-determining dynamics are a pure-JS,
 * double-precision fixed-step integrator (see ./physics.ts) so results are
 * bit-identical across the browser, Node, and the Workers runtime — wasm float
 * results never enter the state hash. Rapier's wasm is still initialized by
 * createSimulation (the surface is async for that reason). See ./simulation.ts.
 */

import type { CourseSpec, GameState, ReplayData, ScoreMetrics } from '@/types';
import { generateCourse as generateCourseImpl } from './generate';
import { createSimulation as createSimulationImpl } from './simulation';
import { verifyReplay as verifyReplayImpl } from './verify';

/** Deterministically build the day's course from its numeric seed. */
export function generateCourse(seed: number, dateKey: string): CourseSpec {
  return generateCourseImpl(seed, dateKey);
}

/** One steering input for a single tick, in raw [-1, 1] space (Core quantizes). */
export interface SteerInput {
  readonly steerX: number;
  readonly steerY: number;
}

/** A live, stepping simulation. Created from a CourseSpec, advanced one fixed
 *  tick at a time. Records its own input timeline into the eventual ReplayData. */
export interface Simulation {
  readonly spec: CourseSpec;
  /** Advance exactly one fixed tick with the given input; returns the new state. */
  step(input: SteerInput): GameState;
  /** Latest snapshot without advancing. */
  getState(): GameState;
  /** The recorded run so far (valid once status is finished/dnf). */
  getReplay(): ReplayData;
  reset(): void;
}

/** Async because Rapier's wasm must initialize first. */
export function createSimulation(spec: CourseSpec): Promise<Simulation> {
  return createSimulationImpl(spec);
}

/**
 * Server-side verification: re-generate the course from replay.seed, re-run the
 * input timeline, and return the authoritative metrics. The Worker compares the
 * result (and stateHash) against the submission. Pure & headless.
 */
export function verifyReplay(
  replay: ReplayData,
): Promise<{ ok: boolean; metrics: ScoreMetrics; stateHash: string }> {
  return verifyReplayImpl(replay);
}
